import { BUY_REPORT_TOOL, BUY_TOOL } from '@vestra/agent-sdk';

/**
 * Every tool name the box can ever give the model, as ONE exact list (B6e, reserved tool names).
 *
 * The union, across capabilities and flags, of what apps/sandbox's native registry registers
 * (harness/native-tools.ts buildNativeToolList, its subagent toolset and a company task's
 * submit_result), plus the two tools the engine adds on its own (ToolSearch, watch) and the one the
 * Anthropic transport adds to the request (tool_search_tool_regex, with deferred loading). The box
 * builds its set at runtime, so the server could not tell a company's function from one of the
 * box's own; this list is what it checks instead. Exact membership only, never a pattern: a
 * function a company declares is refused when its name is here (a clash would otherwise be skipped
 * on the box, the native tool winning, or reach the model twice).
 *
 * Kept equal to the registry by the box's own test (apps/sandbox harness/__tests__/
 * native-tool-catalog.test.ts), which builds the registry under every flag and seam the
 * assembler reads and asserts the union of what it registered is exactly this list.
 */
export const NATIVE_TOOL_NAMES: ReadonlySet<string> = new Set([
  // Files, shell and code
  'Bash', 'execute_code', 'Read', 'Write', 'Edit', 'Patch', 'Glob', 'Grep',
  // Abilities, skills and the person's own MCP servers
  'Skill', 'Ability', 'search_abilities', 'resolve_install_source', 'install_item',
  'resolve_mcp_source', 'connect_mcp', 'mcp_search', 'mcp_invoke', 'mcp_resources',
  // Context, memory and sessions
  'context_query', 'remember', 'session_search', 'session_create',
  'github_pull_request',
  // The assistant's own email address (agent email: only on a box spawned with AGENT_EMAIL_ADDRESS)
  'email_send', 'email_inbox',
  // The agent wallet (only with AGENT_WALLET_V1): buy raises the person's purchase card; buy_report closes it
  BUY_TOOL, BUY_REPORT_TOOL,
  // Asking the person
  'AskUserQuestion', 'request_user_input', 'ExitPlanMode',
  // Todos, background work and helpers
  'TodoCreate', 'TodoUpdate', 'TodoGet', 'TodoList',
  'process', 'TaskOutput', 'TaskStop', 'Monitor',
  'NotebookRead', 'NotebookAppend', 'NotebookCompact',
  'Agent', 'Weave', 'SendMessage', 'ReportToLead', 'StopHelper', 'ListHelpers', 'BoardList', 'BoardClaim', 'BoardDone',
  // The web
  'WebSearch', 'WebFetch',
  // The browser
  'mcp__browser-control__computer_screenshot', 'mcp__browser-control__computer_click', 'mcp__browser-control__computer_type',
  'mcp__browser-control__computer_scroll', 'mcp__browser-control__computer_navigate', 'mcp__browser-control__computer_key',
  'mcp__browser-control__computer_snapshot', 'mcp__browser-control__computer_console', 'mcp__browser-control__computer_dialog',
  'mcp__browser-control__computer_back', 'mcp__browser-control__computer_get_images', 'mcp__browser-control__computer_network',
  'mcp__browser-control__computer_find', 'mcp__browser-control__computer_wait', 'mcp__browser-control__computer_select',
  'mcp__browser-control__computer_upload', 'mcp__browser-control__computer_drag', 'mcp__browser-control__computer_eval',
  'mcp__browser-control__computer_tabs', 'mcp__browser-control__release_browser', 'mcp__browser-control__computer_do',
  // The Mac
  'mcp__os-hands__os_apps', 'mcp__os-hands__os_open', 'mcp__os-hands__os_snapshot', 'mcp__os-hands__os_find',
  'mcp__os-hands__os_screenshot', 'mcp__os-hands__os_ocr', 'mcp__os-hands__os_click', 'mcp__os-hands__os_set_value',
  'mcp__os-hands__os_type', 'mcp__os-hands__os_key', 'mcp__os-hands__os_menu', 'mcp__os-hands__os_scroll',
  'mcp__os-hands__os_wait', 'mcp__os-hands__os_takeover', 'mcp__os-hands__os_release_takeover', 'mcp__os-hands__os_drag',
  'mcp__os-hands__os_run_verb', 'mcp__os-hands__os_list_verbs', 'mcp__os-hands__os_do',
  // Media, outputs, apps and the computer view
  'media', 'media_search', 'present_output', 'announce_preview', 'list_apps', 'stop_app', 'show_on_computer', 'close_computer',
  // A company task's structured result
  'submit_result',
  // Added by the engine itself, after the registry
  'ToolSearch', 'watch',
  // Added by the Anthropic transport to the request (deferred tool loading)
  'tool_search_tool_regex',
]);

/**
 * The MCP server names the box's own tools ride under (`mcp__<name>__<tool>`): the CLI bridge's
 * three (vestra, browser-control, composio) and the Mac's (os-hands). A company MCP server with one
 * of these slugs would mint tool names shaped like the box's own, so the slug is refused.
 */
export const RESERVED_MCP_SERVER_SLUGS: ReadonlySet<string> = new Set(['browser-control', 'composio', 'os-hands', 'vestra']);
